import React, { useState, Suspense } from 'react';
import BEMHelper from '@ynap/bem-helper';
import { CombinedImage } from '@ynap/image';
import ViewportObserver from '@ynap/viewport-observer';
import { withAnalytics, safeBuildEventObj } from '@ynap/analytics';
import Overlay from '@ynap/overlay';
import ZoomedImageCarousel from '@ynap/product-details-page/src/ZoomedImageCarousel/ZoomedImageCarousel.js';
import { upsellNavigation } from '../../index';
import StickyContainer from './StickyContainer/StickyContainer';
import MediaGallery from './MediaGallery/MediaGallery';
import ProductDetails from './ProductDetails/ProductDetails';

const Recommendations = React.lazy(
    () => import(/* webpackChunkName: "Recommendations" */ '@ynap/product-details-page/src/Recommendations/RecommendationsWrapper.js'),
);

const bem = new BEMHelper('FullBleedLayout');

interface Image {
    alt: string;
    id: string;
    url: string;
    srcSet: string;
    modifier: string;
    zoomUrl: string;
    zoomSrcSet: string;
}

interface Thumbnail {
    alt: string;
    url: string;
}

interface Video {
    url: string;
    thumbnail?: Thumbnail;
}

interface Features {
    ymalFirst?: boolean;
}

interface Product {
    images: Image[];
    videos: Video[];
    imageViews?: string[];
    features?: Features;
    colourPartNumber: string;
    modelPartNumber: string;
    recommendationsToFetch: string[];
    imageTemplate: string;
}

interface FullBleedLayoutProps {
    product: Product;
    optimizely?: any; // Replace 'any' with the actual type
    messages?: any; // Replace 'any' with the actual type
    region?: string;
    wasReducedPDP?: boolean;
    config?: any; // Replace 'any' with the actual type
    tracking?: any; // Replace 'any' with the actual type
    bypassWCSRecommendations?: boolean;
    isRTL?: boolean;
}

const FullBleedLayout = (props: FullBleedLayoutProps) => {
    const [isZoomModalVisible, setIsZoomModalVisible] = useState(false);
    const [selectedZoomImageIndex, setSelectedZoomImageIndex] = useState(0);
    const { product, optimizely, messages, region, wasReducedPDP, config, tracking, bypassWCSRecommendations, isRTL } = props;
    const { images, videos, imageViews, features } = product;
    const [stickyImage, ...scrollableImages] = images;

    const zoomSlides = images.map((image) => {
        return {
            component: (
                <CombinedImage
                    key={image.id}
                    className={bem('thumbnailImage', image.modifier)}
                    src={image.zoomUrl}
                    alt={image.alt}
                    primarySrcset={image.zoomSrcSet}
                    imageSrcsetSize="(min-width: 1440px) 100vw, (min-width: 768px) 50vw, 100vw"
                    displaySrc
                    yOffset="100%"
                />
            ),
            video: false,
        };
    });

    return (
        <div className={bem('wrapper')}>
            {/* Sticky image */}
            <StickyContainer image={stickyImage} setIsZoomModalVisible={setIsZoomModalVisible} setSelectedZoomImageIndex={setSelectedZoomImageIndex} />
            {/* media gallery */}
            <div className={bem('mediaGalleryContainer')} data-testid='mediaGalleryContainer'>
                <MediaGallery
                    images={scrollableImages}
                    videos={videos}
                    setIsZoomModalVisible={setIsZoomModalVisible}
                    setSelectedZoomImageIndex={setSelectedZoomImageIndex}
                />
            </div>
            {isZoomModalVisible && (
                <Overlay
                    handleBodyClick={() => setIsZoomModalVisible(false)}
                    onClose={() => setIsZoomModalVisible(false)}
                    className={{ container: 'zoomedImageContainer', button: 'zoomedImageClose', content: 'zoomedImageContent' }}
                >
                    <ZoomedImageCarousel
                        slides={zoomSlides}
                        currentIndex={selectedZoomImageIndex}
                        // TODO check this loading state
                        loading={false}
                        closeZoomModal={() => setIsZoomModalVisible(false)}
                        isRTL={isRTL}
                        zoomedPosition={{ fx: 0.5, fy: 0.5 }}
                    />
                </Overlay>
            )}
            {/* product info */}
            <ProductDetails {...props} safeTrackEvent={safeTrackEvent} />

            {/* recommendations */}
            <div className={bem('recommendationsGrid')}>
                <ViewportObserver className={bem('recommendationsWrapper')} innerKey={product.colourPartNumber}>
                    {(intersected) =>
                        intersected && (
                            <Suspense fallback={''}>
                                <Recommendations
                                    imageViews={imageViews}
                                    messages={messages}
                                    region={region}
                                    colourPartNumber={product.colourPartNumber}
                                    modelPartNumber={product.modelPartNumber}
                                    recommendationsToFetch={product.recommendationsToFetch}
                                    isCarouselVisible={wasReducedPDP ? false : config.head.pdp.enableRecommendationsCarousel}
                                    ymalFirst={features?.ymalFirst || wasReducedPDP}
                                    imageTemplate={product.imageTemplate}
                                    tracking={{
                                        wiw: (product, position) => {
                                            safeTrackEvent(
                                                upsellNavigation,
                                                props,
                                                product,
                                                position,
                                                'upsell - wear it with',
                                                `wear it with - look number ${position}`,
                                            );
                                            /* istanbul ignore next */
                                            optimizely?.track?.('click_upsell_modules');
                                            /* istanbul ignore next */
                                            optimizely?.track?.('clicks_on_WIW');
                                            /* istanbul ignore next */
                                            optimizely?.track?.('pdp_recommendations_click');
                                        },
                                        ymal: (product, position) => {
                                            /* istanbul ignore next */
                                            safeTrackEvent(upsellNavigation, props, product, position, 'upsell - ymal', 'ymal - module');
                                            /* istanbul ignore next */
                                            optimizely?.track?.('click_upsell_modules');
                                            /* istanbul ignore next */
                                            optimizely?.track?.('clicks_on_YMAL');
                                            /* istanbul ignore next */
                                            optimizely?.track?.('pdp_recommendations_click');
                                        },
                                        rv: (product, position) => {
                                            /* istanbul ignore next */
                                            safeTrackEvent(upsellNavigation, props, product, position, 'upsell - rv', 'rv - module');
                                            /* istanbul ignore next */
                                            optimizely?.track?.('click_upsell_modules');
                                            /* istanbul ignore next */
                                            optimizely?.track?.('pdp_rv_click');
                                            /* istanbul ignore next */
                                            optimizely?.track?.('pdp_recommendations_click');
                                        },
                                        trackEvent: tracking.trackEvent,
                                    }}
                                    bypassWCSRecommendations={bypassWCSRecommendations}
                                    useOutfitView={config.head.pdp.useOutfitView}
                                    images={images[0]?.url}
                                    reducedMode={wasReducedPDP}
                                />
                            </Suspense>
                        )
                    }
                </ViewportObserver>
            </div>
        </div>
    );
};

const safeTrackEvent = (buildAnalyticsFunc, props, ...args) => {
    const { analytics, tracking } = props;
    tracking.trackEvent(safeBuildEventObj(buildAnalyticsFunc, analytics, ...args));
};

export default withAnalytics(FullBleedLayout);
